// Elariom's own look, separate from the rest of Monada: warm paper, near-black ink, almost no
// colour, serif section titles and SF Mono for everything else (the system monospace on a Mac;
// JetBrains Mono elsewhere). Lists with dotted dividers instead of cards, flat black buttons.
// Light only. Applied with a ThemeProvider around the Elariom pages, so nothing else changes.

import { createTheme, alpha } from '@mui/material/styles';

export const INK = '#1C1B19';
export const MUTED = '#6F6B63';
export const PAPER = '#F6F5F1';
export const SURFACE = '#FBFAF7';
export const HAIRLINE = '#DDD8CE';
export const GOOD = '#3E7B57';
export const WARN = '#A86B12';
export const BAD = '#A3402F';

export const MONO = 'ui-monospace, "SF Mono", SFMono-Regular, "JetBrains Mono", Menlo, Consolas, monospace';
export const SERIF = '"Newsreader", "Source Serif 4", Georgia, "Times New Roman", serif';

// Fonts: SF Mono is built into macOS; the serif and the monospace fallback come from Google Fonts.
export function loadElariomFonts() {
    if (typeof document === 'undefined' || document.getElementById('elariom-fonts')) return;
    const link = document.createElement('link');
    link.id = 'elariom-fonts';
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Newsreader:opsz,wght@6..72,400;6..72,600;6..72,700&family=JetBrains+Mono:wght@400;500;600&display=swap';
    document.head.appendChild(link);
}

// Dotted divider, the way the reference lists separate rows.
export const dotted = `1px dotted ${alpha(INK, 0.28)}`;

const theme = createTheme({
    palette: {
        mode: 'light',
        primary: { main: INK, contrastText: '#FFFFFF' },
        secondary: { main: MUTED },
        success: { main: GOOD },
        warning: { main: WARN },
        error: { main: BAD },
        info: { main: '#4A5A6A' },
        text: { primary: INK, secondary: MUTED },
        background: { default: PAPER, paper: SURFACE },
        divider: HAIRLINE,
        panel1: SURFACE,
        border1: HAIRLINE,
        'monada-button': { main: INK, contrastText: '#FFFFFF', dark: '#000000', light: '#3A3833' },
    },
    shape: { borderRadius: 4 },
    typography: {
        fontFamily: MONO,
        fontSize: 13,
        button: { textTransform: 'none', fontWeight: 500, letterSpacing: 0 },
    },
    components: {
        MuiCssBaseline: { styleOverrides: { body: { background: PAPER } } },
        MuiPaper: {
            defaultProps: { elevation: 0 },
            styleOverrides: { root: { backgroundImage: 'none' }, outlined: { borderColor: HAIRLINE } },
        },
        MuiButton: {
            defaultProps: { disableElevation: true },
            styleOverrides: {
                root: { borderRadius: 4, fontFamily: MONO },
                containedPrimary: { background: INK, '&:hover': { background: '#000' } },
                outlined: { borderColor: alpha(INK, 0.35), color: INK, background: '#FFFFFF', '&:hover': { borderColor: INK, background: '#FFFFFF' } },
                text: { color: INK },
            },
        },
        // Dialogs and side panels render outside the page, where Monada's global font applies:
        // set SF Mono again inside them, keeping the serif titles.
        MuiDialog: { styleOverrides: { paper: { background: PAPER, borderRadius: 6, border: `1px solid ${HAIRLINE}`, fontFamily: MONO, '& .MuiTypography-root:not(.elariom-serif):not(.MuiDialogTitle-root)': { fontFamily: MONO }, '& button, & input:not([type=date]), & textarea, & select, & a': { fontFamily: `${MONO} !important` } } } },
        MuiDialogTitle: { styleOverrides: { root: { fontFamily: SERIF, fontWeight: 600, fontSize: '22px' } } },
        MuiTableCell: { styleOverrides: { root: { fontFamily: MONO, borderBottom: dotted }, head: { color: MUTED, fontWeight: 500 } } },
        MuiTab: { styleOverrides: { root: { fontFamily: MONO, color: MUTED, '&.Mui-selected': { color: INK } } } },
        MuiTabs: { styleOverrides: { indicator: { background: INK, height: 1 } } },
        MuiAlert: { styleOverrides: { root: { borderRadius: 4, fontFamily: MONO } } },
        MuiTooltip: { styleOverrides: { tooltip: { fontFamily: MONO, background: INK, fontSize: '12px' } } },
        MuiOutlinedInput: { styleOverrides: { root: { background: '#FFFFFF', fontFamily: MONO }, notchedOutline: { borderColor: alpha(INK, 0.25) } } },
        MuiToggleButton: { styleOverrides: { root: { fontFamily: MONO, color: MUTED, borderColor: alpha(INK, 0.2), '&.Mui-selected': { background: INK, color: '#FFFFFF', '&:hover': { background: '#000' } } } } },
        MuiSwitch: { styleOverrides: { switchBase: { '&.Mui-checked': { color: '#FFFFFF', '& + .MuiSwitch-track': { background: INK, opacity: 1 } } } } },
        MuiCheckbox: { styleOverrides: { root: { color: alpha(INK, 0.45), '&.Mui-checked': { color: INK } } } },
        MuiLinearProgress: { styleOverrides: { root: { background: HAIRLINE }, bar: { background: INK } } },
        MuiDrawer: { styleOverrides: { paper: { background: PAPER, fontFamily: MONO, '& .MuiTypography-root:not(.elariom-serif)': { fontFamily: MONO }, '& button, & input:not([type=date]), & a': { fontFamily: `${MONO} !important` } } } },
    },
});

export default theme;
